import { PREVIEW_LONG_EDGE, PREVIEW_MAX_IMAGE_BYTES } from '@/config/constants';

export type LocalPreview = { blobUrl: string; width: number; height: number };

const NO_BITMAP = new Set(['image/heic', 'image/heif', 'image/vnd.adobe.photoshop', 'image/tiff', 'application/postscript', 'image/x-adobe-dng']);

/** §3.2 step 5 — local preview + intrinsic size; never throws (null on failure). */
export async function makePreview(file: File, mime: string): Promise<LocalPreview | null> {
  try {
    if (mime.startsWith('image/') && !NO_BITMAP.has(mime) && file.size <= PREVIEW_MAX_IMAGE_BYTES) return await imagePreview(file);
    if (mime.startsWith('video/')) return await videoPreview(file);
  } catch {
    /* fall through */
  }
  return null;
}

async function imagePreview(file: File): Promise<LocalPreview | null> {
  if (typeof createImageBitmap !== 'function') return null;
  const full = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const { width, height } = full;
  const scale = Math.min(1, PREVIEW_LONG_EDGE / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));
  let blob: Blob;
  if (typeof OffscreenCanvas !== 'undefined') {
    const c = new OffscreenCanvas(w, h);
    const ctx = c.getContext('2d')!;
    ctx.drawImage(full, 0, 0, w, h);
    blob = await c.convertToBlob({ type: 'image/jpeg', quality: 0.8 });
  } else {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    c.getContext('2d')!.drawImage(full, 0, 0, w, h);
    blob = await new Promise<Blob>((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/jpeg', 0.8));
  }
  full.close();
  return { blobUrl: URL.createObjectURL(blob), width, height };
}

function videoPreview(file: File): Promise<LocalPreview | null> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.muted = true;
    v.preload = 'metadata';
    v.playsInline = true;
    const url = URL.createObjectURL(file);
    let done = false;
    const finish = (r: LocalPreview | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      v.removeAttribute('src');
      v.load();
      resolve(r);
    };
    const timer = setTimeout(() => finish(v.videoWidth ? { blobUrl: '', width: v.videoWidth, height: v.videoHeight } : null), 3000);
    v.onerror = () => finish(null);
    v.onloadedmetadata = () => {
      v.currentTime = Math.max(0, (v.duration || 0) * 0.1);
    };
    v.onseeked = () => {
      const width = v.videoWidth;
      const height = v.videoHeight;
      if (!width) return finish(null);
      const scale = Math.min(1, PREVIEW_LONG_EDGE / Math.max(width, height));
      const c = document.createElement('canvas');
      c.width = Math.round(width * scale);
      c.height = Math.round(height * scale);
      c.getContext('2d')!.drawImage(v, 0, 0, c.width, c.height);
      c.toBlob((b) => finish({ blobUrl: b ? URL.createObjectURL(b) : '', width, height }), 'image/jpeg', 0.8);
    };
    v.src = url;
  });
}

/** Give the main thread back between files (§3.2 step 5). */
export function yieldToMain(): Promise<void> {
  const sch = (globalThis as unknown as { scheduler?: { yield?: () => Promise<void> } }).scheduler;
  if (sch?.yield) return sch.yield();
  return new Promise((r) => setTimeout(r, 0));
}
